import { useActionState } from "react";
import { createTopicAction } from "@/actions/topics";
import { Button } from "@/components/Button";
import { CodeCell, FormCell } from "@/components/FormCell";
import styles from "@/components/FormRow.module.css";
import { LinkButton } from "@/components/LinkButton";
import { useCodeFromLabel } from "@/components/useCodeFromLabel";
import { initialFormState } from "@/lib/formState";
import { useFormRedirect } from "@/lib/useFormRedirect";

const FORM_ID = "topic-draft";

type TopicDraftRowProps = {
  // The listing owns the rows, so a save has to tell it to read again.
  onSaved: () => void;
};

export const TopicDraftRow = ({ onSaved }: TopicDraftRowProps) => {
  const [state, formAction, isPending] = useActionState(
    createTopicAction,
    initialFormState,
  );

  useFormRedirect(state, onSaved);

  // The code derives from the label until someone edits it, so the row owns
  // both fields rather than each cell owning its own.
  const { label, code, setLabel, setCode } = useCodeFromLabel(
    state.values.label,
    state.values.code,
  );

  return (
    <tr>
      <CodeCell
        form={FORM_ID}
        name="code"
        label="Code"
        isRowHeader
        placeholder="FITNESS"
        value={code}
        onValueChange={setCode}
        errors={state.fieldErrors.code}
      />
      <FormCell
        form={FORM_ID}
        name="label"
        label="Label"
        placeholder="Fitness"
        value={label}
        onValueChange={setLabel}
        errors={state.fieldErrors.label}
      />
      <FormCell
        form={FORM_ID}
        name="icon"
        label="Icon"
        placeholder="dumbbell"
        defaultValue={state.values.icon}
        errors={state.fieldErrors.icon}
      />
      <FormCell
        form={FORM_ID}
        name="sortOrder"
        label="Sort order"
        inputMode="numeric"
        defaultValue={state.values.sortOrder ?? "0"}
        errors={state.fieldErrors.sortOrder}
      />
      {/* State and the actions column, neither of which a draft has. */}
      <td className={styles.actions} colSpan={2}>
        {/* A form may not be a child of tbody, but it may sit inside a cell —
            the inputs above associate to it by id. */}
        <form id={FORM_ID} action={formAction} hidden />
        <Button
          type="submit"
          form={FORM_ID}
          variant="primary"
          disabled={isPending}
        >
          {isPending ? "Creating..." : "Create"}
        </Button>
        <LinkButton href="/topics" variant="secondary">
          Cancel
        </LinkButton>
      </td>
    </tr>
  );
};
